import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAllAdminProducts } from "@/hooks/useProducts";
import {
  Package, Eye, EyeOff, CheckCircle, XCircle, Tag,
  AlertTriangle, ShoppingCart, Clock, TrendingUp,
} from "lucide-react";
import { format, subDays, startOfDay } from "date-fns";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const StatCard = ({ label, value, icon: Icon, color }: { label: string; value: number | string; icon: any; color: string }) => (
  <div className="bg-card rounded-lg border border-border p-5 flex items-center gap-4">
    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
      <Icon className="w-5 h-5" />
    </div>
    <div>
      <p className="text-2xl font-display font-bold">{value}</p>
      <p className="text-sm text-muted-foreground font-body">{label}</p>
    </div>
  </div>
);

const LOW_STOCK_THRESHOLD = 5;

const statusColors: Record<string, string> = {
  pending: "bg-yellow-100 text-yellow-700",
  processing: "bg-blue-100 text-blue-700",
  completed: "bg-green-100 text-green-700",
  cancelled: "bg-destructive/10 text-destructive",
  refunded: "bg-muted text-muted-foreground",
};

const CAT_PAGE_SIZE = 10;

const AdminDashboard = () => {
  const { data: products, isLoading } = useAllAdminProducts();
  const [catPage, setCatPage] = useState(1);

  const { data: recentOrders, isLoading: ordersLoading } = useQuery({
    queryKey: ["admin-recent-orders"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("orders")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(10);
      if (error) throw error;
      return data;
    },
  });

  const { data: allOrders } = useQuery({
    queryKey: ["admin-all-orders-chart"],
    queryFn: async () => {
      const since = subDays(new Date(), 30).toISOString();
      const { data, error } = await supabase
        .from("orders")
        .select("created_at, total")
        .gte("created_at", since)
        .order("created_at");
      if (error) throw error;
      return data;
    },
  });

  const chartData = useMemo(() => {
    const days: Record<string, number> = {};
    for (let i = 29; i >= 0; i--) {
      const d = format(subDays(new Date(), i), "MMM dd");
      days[d] = 0;
    }
    allOrders?.forEach((o) => {
      const d = format(new Date(o.created_at), "MMM dd");
      if (d in days) days[d] += Number(o.total);
    });
    return Object.entries(days).map(([date, revenue]) => ({ date, revenue: Math.round(revenue * 100) / 100 }));
  }, [allOrders]);

  const totalRevenue30d = chartData.reduce((s, d) => s + d.revenue, 0);

  const stats = useMemo(() => {
    if (!products) return null;
    const published = products.filter((p) => p.published).length;
    const hidden = products.length - published;
    const inStock = products.filter((p) => p.in_stock).length;
    const outOfStock = products.length - inStock;
    const onSale = products.filter((p) => p.sale_price != null).length;

    const catCounts: Record<string, number> = {};
    products.forEach((p) => {
      if (p.categories) {
        p.categories.split(",").forEach((c) => {
          const trimmed = c.trim().split(">").pop()?.trim();
          if (trimmed) catCounts[trimmed] = (catCounts[trimmed] || 0) + 1;
        });
      } else {
        catCounts["Uncategorized"] = (catCounts["Uncategorized"] || 0) + 1;
      }
    });
    const sortedCats = Object.entries(catCounts).sort((a, b) => b[1] - a[1]);

    const lowStock = products
      .filter((p) => p.stock != null && p.stock <= LOW_STOCK_THRESHOLD && p.in_stock)
      .sort((a, b) => (a.stock ?? 0) - (b.stock ?? 0));

    return { total: products.length, published, hidden, inStock, outOfStock, onSale, sortedCats, lowStock };
  }, [products]);

  if (isLoading || !stats) {
    return <div className="text-muted-foreground text-center py-20">Loading…</div>;
  }

  return (
    <div>
      <h1 className="font-display text-2xl font-bold mb-6">Dashboard</h1>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
        <StatCard label="Total Products" value={stats.total} icon={Package} color="bg-primary/10 text-primary" />
        <StatCard label="Published" value={stats.published} icon={Eye} color="bg-green-100 text-green-700" />
        <StatCard label="Hidden" value={stats.hidden} icon={EyeOff} color="bg-muted text-muted-foreground" />
        <StatCard label="In Stock" value={stats.inStock} icon={CheckCircle} color="bg-green-100 text-green-700" />
        <StatCard label="Out of Stock" value={stats.outOfStock} icon={XCircle} color="bg-destructive/10 text-destructive" />
        <StatCard label="On Sale" value={stats.onSale} icon={Tag} color="bg-blue-100 text-blue-700" />
      </div>

      {/* Revenue Chart */}
      <div className="bg-card rounded-lg border border-border p-5 mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-lg font-semibold flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-muted-foreground" />Revenue (Last 30 Days)
          </h2>
          <span className="font-display text-xl font-bold text-primary">£{totalRevenue30d.toFixed(2)}</span>
        </div>
        <div className="h-48">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} interval={4} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `£${v}`} />
              <Tooltip formatter={(v: number) => [`£${v.toFixed(2)}`, "Revenue"]} />
              <Bar dataKey="revenue" fill="hsl(var(--primary))" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-8">
        {/* Recent Orders */}
        <div className="bg-card rounded-lg border border-border overflow-hidden">
          <div className="px-5 py-4 border-b border-border flex items-center justify-between">
            <h2 className="font-display text-lg font-semibold flex items-center gap-2">
              <ShoppingCart className="w-5 h-5 text-muted-foreground" />Recent Orders
            </h2>
            <Link to="/admin/orders" className="text-xs text-primary hover:underline font-body">View all</Link>
          </div>
          {ordersLoading ? (
            <div className="p-8 text-center text-muted-foreground text-sm">Loading…</div>
          ) : !recentOrders?.length ? (
            <div className="p-8 text-center text-muted-foreground text-sm font-body">No orders yet</div>
          ) : (
            <div className="divide-y divide-border">
              {recentOrders.map((order) => (
                <Link key={order.id} to={`/admin/orders/${order.id}`} className="flex items-center justify-between px-5 py-3 hover:bg-secondary/30 transition-colors">
                  <div className="min-w-0 flex-1">
                    <p className="font-body text-sm font-medium truncate">{order.customer_name || order.customer_email || "Guest"}</p>
                    <p className="text-xs text-muted-foreground font-body flex items-center gap-1">
                      <Clock className="w-3 h-3" />{format(new Date(order.created_at), "dd MMM yyyy, HH:mm")}
                    </p>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${statusColors[order.status] || "bg-secondary text-secondary-foreground"}`}>{order.status}</span>
                    <span className="font-body text-sm font-semibold w-16 text-right">£{order.total.toFixed(2)}</span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Low Stock Alerts */}
        <div className="bg-card rounded-lg border border-border overflow-hidden">
          <div className="px-5 py-4 border-b border-border flex items-center justify-between">
            <h2 className="font-display text-lg font-semibold flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-500" />Low Stock Alerts
            </h2>
            <span className="text-xs text-muted-foreground font-body">≤ {LOW_STOCK_THRESHOLD} units</span>
          </div>
          {stats.lowStock.length === 0 ? (
            <div className="p-8 text-center text-sm font-body text-muted-foreground">
              <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-500 opacity-60" />All products are well stocked
            </div>
          ) : (
            <div className="divide-y divide-border">
              {stats.lowStock.map((p) => (
                <div key={p.id} className="flex items-center justify-between px-5 py-3">
                  <div className="min-w-0 flex-1">
                    <Link to={`/admin/edit/${p.id}`} className="font-body text-sm font-medium truncate block hover:text-primary transition-colors">{p.name}</Link>
                    <p className="text-xs text-muted-foreground font-body">{p.sku || "No SKU"}</p>
                  </div>
                  <span className={`inline-flex items-center gap-1.5 text-xs font-body font-semibold shrink-0 ${(p.stock ?? 0) === 0 ? "text-destructive" : "text-yellow-600"}`}>
                    <AlertTriangle className="w-3 h-3" />{p.stock ?? 0} left
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Category breakdown */}
      {(() => {
        const totalCatPages = Math.ceil(stats.sortedCats.length / CAT_PAGE_SIZE);
        const pagedCats = stats.sortedCats.slice((catPage - 1) * CAT_PAGE_SIZE, catPage * CAT_PAGE_SIZE);
        return (
          <div className="bg-card rounded-lg border border-border overflow-hidden">
            <div className="px-5 py-4 border-b border-border flex items-center justify-between">
              <h2 className="font-display text-lg font-semibold">Products by Category</h2>
              <span className="text-xs text-muted-foreground font-body">{stats.sortedCats.length} categories</span>
            </div>
            <div className="divide-y divide-border">
              {pagedCats.map(([cat, count]) => (
                <div key={cat} className="flex items-center justify-between px-5 py-3">
                  <span className="font-body text-sm">{cat}</span>
                  <div className="flex items-center gap-3">
                    <div className="w-32 h-2 rounded-full bg-secondary overflow-hidden hidden sm:block">
                      <div className="h-full rounded-full bg-primary/60" style={{ width: `${Math.round((count / stats.total) * 100)}%` }} />
                    </div>
                    <span className="text-sm font-body font-medium text-muted-foreground w-8 text-right">{count}</span>
                  </div>
                </div>
              ))}
            </div>
            {totalCatPages > 1 && (
              <div className="px-5 py-3 border-t border-border flex items-center justify-between">
                <span className="text-xs text-muted-foreground font-body">
                  Page {catPage} of {totalCatPages}
                </span>
                <div className="flex gap-2">
                  <button onClick={() => setCatPage((p) => Math.max(1, p - 1))} disabled={catPage === 1} className="px-3 py-1 text-xs rounded border border-border disabled:opacity-40 hover:bg-secondary transition-colors font-body">Previous</button>
                  <button onClick={() => setCatPage((p) => Math.min(totalCatPages, p + 1))} disabled={catPage === totalCatPages} className="px-3 py-1 text-xs rounded border border-border disabled:opacity-40 hover:bg-secondary transition-colors font-body">Next</button>
                </div>
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
};

export default AdminDashboard;
